import React, { useState, useEffect, useMemo } from 'react';
import {
  FaTimes,
  FaFlag,
  FaShareAlt,
  FaComment,
  FaShoppingCart,
  FaImage,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
} from 'react-icons/fa';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import MessageDetailModal from '../components/MessageDetailModal';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { storage } from './firebaseConfig';
import ReportDialog from '../components/safety/ReportDialog';
import { LISTING_REPORT_REASONS, reportListing } from '../services/safetyService';
import './ListingDetailModal.css';

export default function ListingDetailModal({ saleItem, onClose }) {
  const { user } = useAuth();
  const rawImages = useMemo(() => saleItem.images || [], [saleItem.images]);
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    setLoading(true);
    if (!rawImages.length) {
      setUrls([]);
      setLoading(false);
      return;
    }
    Promise.all(
      rawImages.map(pathOrUrl =>
        pathOrUrl.startsWith('http')
          ? Promise.resolve(pathOrUrl)
          : getDownloadURL(storageRef(storage, pathOrUrl))
      )
    )
      .then(setUrls)
      .catch(err => {
        console.error('Image load error:', err);
        setUrls([]);
      })
      .finally(() => {
        setLoading(false);
        setIndex(0);
      });
  }, [rawImages]);

  const prev = () => setIndex(i => (i + urls.length - 1) % urls.length);
  const next = () => setIndex(i => (i + 1) % urls.length);

  const handleShare = () => {
    const data = { title: saleItem.title, text: saleItem.description || '', url: window.location.href };
    if (navigator.share) navigator.share(data);
    else {
      navigator.clipboard.writeText(data.url);
      alert('Link copied to clipboard');
    }
  };

  return (
    <>
      <Modal
        show
        onHide={onClose}
        centered
        dialogClassName="detail-modal-dialog"
        contentClassName="detail-modal-content"
      >
        <Modal.Header className="detail-header-row">
          <Button
            variant="link"
            className="detail-report"
            onClick={() => user ? setShowReport(true) : alert('Sign in to report this listing.')}
            aria-label="Report listing"
          >
            <FaFlag />
          </Button>
          <Button variant="link" className="detail-close" onClick={onClose}>
            <FaTimes />
          </Button>
        </Modal.Header>

        <Modal.Body className="detail-modal-body">
          <div className="slider">
            {loading ? (
              <Spinner animation="border" variant="light" />
            ) : urls.length === 0 ? (
              <div className="detail-placeholder">
                <FaImage size={48} />
                <p>No photos</p>
              </div>
            ) : (
              <>
                <button className="nav prev" onClick={prev}><FaChevronLeft /></button>
                <img
                  src={urls[index]}
                  alt={`slide ${index}`}
                  className="detail-image"
                  onClick={() => setFullscreen(true)}
                />
                <button className="nav next" onClick={next}><FaChevronRight /></button>
                <div className="detail-carousel-indicator">{index + 1}/{urls.length}</div>
              </>
            )}
          </div>

          <div className="detail-info">
            <h2 className="detail-title">{saleItem.title}</h2>
            <p className="detail-price">${parseFloat(saleItem.price).toFixed(0)}</p>
            {saleItem.description && <p className="detail-desc">{saleItem.description}</p>}
            <p className="detail-location">
              <strong>Location:</strong> {saleItem.city}, {saleItem.state} {saleItem.zipCode}
            </p>
          </div>
        </Modal.Body>

        <Modal.Footer className="detail-modal-footer">
          <Button variant="link" className="action-btn" onClick={handleShare}>
            <FaShareAlt /><span>Share</span>
          </Button>
          <Button variant="link" className="action-btn" onClick={() => setShowMessage(true)}>
            <FaComment /><span>Message</span>
          </Button>
          <Button variant="link" className="action-btn disabled">
            <FaShoppingCart /><span>Buy Now</span>
          </Button>
        </Modal.Footer>
      </Modal>

      {fullscreen && urls[index] && (
        <div className="detail-fullscreen">
          <div className="fullscreen-header">
            <button className="fullscreen-back" onClick={() => setFullscreen(false)}>
              <FaChevronDown />
            </button>
          </div>
          <div className="fullscreen-content">
            <button className="fullscreen-nav prev" onClick={prev}><FaChevronLeft /></button>
            <img src={urls[index]} alt="fullscreen" className="detail-fullscreen-image" />
            <button className="fullscreen-nav next" onClick={next}><FaChevronRight /></button>
          </div>
        </div>
      )}

      <ReportDialog
        show={showReport}
        onClose={() => setShowReport(false)}
        title="Report Shoparound listing"
        reasons={LISTING_REPORT_REASONS}
        onSubmit={(reason, otherReason) => reportListing({
          uid: user?.uid,
          listingId: saleItem.id || saleItem.forsaleId,
          reason,
          otherReason,
        })}
      />

      {showMessage && (
        <MessageDetailModal
          show={showMessage}
          onClose={() => setShowMessage(false)}
          forsaleId={saleItem.id}
          receiverId={saleItem.receiverId}
          interestedId={user?.uid}
          conversationId={null}
        />
      )}
    </>
  );
}
